import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { usePerformance } from "../../../hooks/features/usePerformance";
import { useAuth } from "../auth/AuthGuard";
import { PERMISSIONS } from "../../../constants";
import useNotification from "../../../hooks/common/useNotification";
import useConfirmDialog from "../../../hooks/common/useConfirmDialog";
import ErrorBoundary from "../../common/ErrorBoundary";

const ObjectiveList = ({ type = 'all', title = 'Performance Objectives' }) => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { showConfirmDialog } = useConfirmDialog();
  
  const {
    objectives,
    pagination,
    loading,
    fetchObjectives,
    fetchMyObjectives,
    fetchPendingApprovals,
    deleteObjective
  } = usePerformance();

  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [quarterFilter, setQuarterFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [approvalFilter, setApprovalFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // State for actions menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);

  // Fetch data based on type
  useEffect(() => {
    const fetchData = async () => {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        quarter: quarterFilter,
        year: yearFilter,
        is_approved: approvalFilter
      };

      try {
        switch (type) {
          case 'my':
            await fetchMyObjectives(params);
            break;
          case 'pending':
            await fetchPendingApprovals(params);
            break;
          default:
            await fetchObjectives(params);
            break;
        }
      } catch (error) {
        console.error('Failed to fetch objectives:', error);
      }
    };

    fetchData();
  }, [type, page, rowsPerPage, searchTerm, quarterFilter, yearFilter, approvalFilter]);

  // Handle actions menu
  const handleMenuOpen = (event, objective) => {
    setAnchorEl(event.currentTarget);
    setSelectedObjective(objective);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedObjective(null);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedObjective) return;

    const confirmed = await showConfirmDialog(
      'Delete Objective',
      `Are you sure you want to delete the objective "${selectedObjective.title}"?`
    );

    if (confirmed) {
      try {
        await deleteObjective(selectedObjective.id);
        handleMenuClose();
      } catch (error) {
        console.error('Failed to delete objective:', error);
      }
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
      quarter: quarterFilter,
      year: yearFilter,
      is_approved: approvalFilter
    };

    try {
      switch (type) {
        case 'my':
          await fetchMyObjectives(params);
          break;
        case 'pending':
          await fetchPendingApprovals(params);
          break;
        default:
          await fetchObjectives(params);
          break;
      }
    } catch (error) {
      console.error('Failed to refresh objectives:', error);
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtered objectives
  const filteredObjectives = useMemo(() => {
    let filtered = objectives || [];
    
    if (searchTerm) {
      filtered = filtered.filter(objective => 
        objective.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        objective.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        objective.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        objective.user?.sur_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [objectives, searchTerm]);

  // Calculate appraisal progress
  const getAppraisalProgress = (appraisalStats) => {
    if (!appraisalStats || appraisalStats.total === 0) return 0;
    return Math.round((appraisalStats.hr_reviewed / appraisalStats.total) * 100);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage performance objectives and track completion progress
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          {hasPermission(PERMISSIONS.PERFORMANCE_CREATE) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/performance/objectives/create')}
            >
              Create Objective
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search objectives..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Quarter</InputLabel>
                <Select
                  value={quarterFilter}
                  onChange={(e) => setQuarterFilter(e.target.value)}
                  label="Quarter"
                >
                  <MenuItem value="">All Quarters</MenuItem>
                  <MenuItem value="Q1">Q1</MenuItem>
                  <MenuItem value="Q2">Q2</MenuItem>
                  <MenuItem value="Q3">Q3</MenuItem>
                  <MenuItem value="Q4">Q4</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={yearFilter}
                onChange={(e) => setYearFilter(parseInt(e.target.value))}
                inputProps={{ min: 2020, max: 2030 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Approval</InputLabel>
                <Select
                  value={approvalFilter}
                  onChange={(e) => setApprovalFilter(e.target.value)}
                  label="Approval"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Approved</MenuItem>
                  <MenuItem value="false">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm("");
                    setQuarterFilter("");
                    setYearFilter(new Date().getFullYear());
                    setApprovalFilter("");
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Objectives Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Quarter</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Appraisals</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredObjectives.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No objectives found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredObjectives.map((objective) => (
                  <TableRow key={objective.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {objective.user?.first_name} {objective.user?.sur_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {objective.user?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                          {objective.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {objective.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={objective.implementation_quarter}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {objective.fiscalYear?.year}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={objective.is_approved ? <ApprovedIcon /> : <PendingIcon />}
                        label={objective.is_approved ? 'Approved' : 'Pending'}
                        size="small"
                        color={objective.is_approved ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AssessmentIcon fontSize="small" />
                        <Typography variant="body2">
                          {objective.appraisal_stats?.total || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 100 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {getAppraisalProgress(objective.appraisal_stats)}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {objective.appraisal_stats?.hr_reviewed || 0}/{objective.appraisal_stats?.total || 0}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getAppraisalProgress(objective.appraisal_stats)}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(objective.created_at), 'dd/MM/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, objective)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={pagination.totalItems || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate(`/performance/objectives/${selectedObjective?.id}`);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        {hasPermission(PERMISSIONS.PERFORMANCE_UPDATE) && (
          <MenuItem
            onClick={() => {
              navigate(`/performance/objectives/${selectedObjective?.id}/edit`);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        
        {hasPermission(PERMISSIONS.PERFORMANCE_DELETE) && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

// Wrapper component with error boundary
const ObjectiveListWithErrorBoundary = (props) => {
  return (
    <ErrorBoundary
      fallback={
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              Unable to load objectives
            </Typography>
            <Typography variant="body2">
              There was an error loading the objective data. Please try refreshing the page or contact support if the problem persists.
            </Typography>
          </Alert>
        </Box>
      }
    >
      <ObjectiveList {...props} />
    </ErrorBoundary>
  );
};

export default ObjectiveListWithErrorBoundary;
